import {
  DocumentPurchaseModel,
  DocumentPurchaseMutationInput,
} from 'api-hooks/order/travel-document';
import { TravelDocumentSalesModel } from 'api-hooks/sale/travel-document';
import formSetErrors from 'common/helpers/form-setError';
import notification from 'components/common/notification';
import { Form, FormState } from 'components/elements';
import FormHeader from 'components/widgets/form-header';
import useApprovalAction from 'hooks/use-approval-action';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useResolveApproval from 'hooks/use-resolve-approval';
import useRevertDraftAction from 'hooks/use-revert-draft-action';
import useRevertVoidAction from 'hooks/use-revert-void-action';
import useVoidAction from 'hooks/use-void-action';
import TitleStatus from 'legacy-modules/common/title-status';
import OtherPriceComponent from 'legacy-modules/order/component/other-price-component';
import ParentOrderRelatedTable from 'legacy-modules/order/component/parent-children-purchase-table';
import SaleRelatedTable from 'legacy-modules/order/component/sale-related-table';
import SalePurchaseRefundTable from 'legacy-modules/refund/components/sale-purchase-table';
import {
  HalfContainer,
  SectionContainer,
  SeparatorVertical,
} from 'legacy-modules/styles';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import DocumentDetailField from './document-detail-field';
import { DocumentPurchaseFormType } from './form-type';
import InformationField from './information-field';
import PurchaseDetailField from './purchase-detail-field';
import DocumentPurchaseSelectInput from './select-input';
import BottomSection from './summary-price/bottom-section';
import useGetDefaultValues from '../hooks/use-get-default-values';
import { FormContent, FormDivider } from '../style';

interface Props {
  travelDocument?: DocumentPurchaseModel;
  onSubmit: (
    input: DocumentPurchaseMutationInput,
    form: ReturnType<typeof useForm>,
  ) => Promise<DocumentPurchaseModel | undefined>;
  renderError?: React.ReactNode;
  travelDocumentSale?: TravelDocumentSalesModel;
  defaultValues?: DocumentPurchaseFormType;
}

export default function TravelDocumentOrderForm(props: Props) {
  const { t } = useTranslation();
  const { travelDocument, renderError, travelDocumentSale } = props;
  const { asPath } = useRouter();

  const defaultValues = useGetDefaultValues({
    documentPurchase: travelDocument,
    documentSale: travelDocumentSale,
    defaultValues: props.defaultValues,
  });

  const methods = useForm({
    defaultValues,
  });

  const onSubmit = React.useCallback(
    async (values: DocumentPurchaseFormType) => {
      const {
        agentStatus,
        tempTransactionAt,
        invoiceId,
        documentPurchases,
        remainingTransactionQuota,
        isExceedingQuota,
        ...rest
      } = values;
      try {
        const needApproval =
          values.transactionAt.getTime() !== tempTransactionAt.getTime();
        const dp = documentPurchases.map((dp) => {
          return {
            ...dp,
            visaDocuments: dp.visaDocuments.map((vd) => vd.id),
          };
        });
        const result = {
          ...rest,
          needApproval,
          documentPurchases: dp,
        };

        await props.onSubmit(result, methods as any);
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods, props],
  );

  const permission = useGetCrudPermission('document-purchases');
  const { onVoid, isActive, voidMutate, voidPermission } = useVoidAction(
    'document-purchases',
    travelDocument,
  );
  const { resolverButton } = useResolveApproval(
    'document-purchases',
    travelDocument,
  );
  const { requestApprovalButton } = useApprovalAction(
    'document-purchases',
    travelDocument,
  );
  const { revertVoidButton } = useRevertVoidAction(
    'document-purchases',
    travelDocument,
  );
  const { revertDraftButton } = useRevertDraftAction(
    'document-purchases',
    travelDocument,
  );
  const onRenderRightComponent = React.useMemo(() => {
    if (travelDocument) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {revertDraftButton}
          {requestApprovalButton}
          {resolverButton}
          {revertVoidButton}
        </div>
      );
    }
    return undefined;
  }, [
    requestApprovalButton,
    resolverButton,
    revertDraftButton,
    revertVoidButton,
    travelDocument,
  ]);

  const onRelatedOrderAfterChange = React.useCallback(
    (value: DocumentPurchaseModel) => {
      methods.setValue('parentRefId', value.refId);
      methods.setValue('agentId', value.agent?.id || '');
      methods.setValue('invoiceId', value.sale?.id || '');
      methods.setValue('tourId', value.tour?.id || '');
      // @ts-ignore
      methods.setValue('type', value.type || '');
      methods.setValue('paymentChannelId', value.paymentChannel?.id || '');
    },
    [methods],
  );

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!travelDocument}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      customViewName={
        travelDocument &&
        `${t('common:travel_document_purchase')} - ${travelDocument.number}`
      }
      keyName={asPath}
    >
      <FormContent>
        {renderError}
        <FormState>
          {({ editable }) => (
            <FormHeader
              data={travelDocument}
              onDelete={onVoid}
              noSubmit
              noCancel
              noDelete={editable || !voidPermission}
              deleteText={t('common:void')}
              title={
                travelDocument ? (
                  <TitleStatus
                    type="purchase"
                    title={`${t('common:travel_document_purchase')} - ${
                      travelDocument.number
                    }`}
                    status={travelDocument.status}
                  />
                ) : (
                  t('common:add_extra', {
                    extra: t('common:travel_document_purchase'),
                  })
                )
              }
              isDeleteLoading={voidMutate.isLoading}
              noEdit={!isActive || !permission.update}
              onRenderRightComponent={onRenderRightComponent}
            />
          )}
        </FormState>
        <div>
          {travelDocument &&
            !!(travelDocument?.releasePurchases || []).length && (
              <SalePurchaseRefundTable data={travelDocument.releasePurchases} />
            )}
          {travelDocument && (
            <SaleRelatedTable
              data={travelDocument.sale}
              navigation={{
                sale: 'TravelDocumentSalesView',
                purchase: 'DocumentPurchasesView',
              }}
            />
          )}
          {travelDocument &&
            (!!travelDocument.parentPurchase ||
              !!(travelDocument.childrenPurchases || []).length) && (
              <ParentOrderRelatedTable
                data={travelDocument}
                navigation="DocumentPurchasesView"
              />
            )}
          <SectionContainer>
            <HalfContainer>
              <DocumentPurchaseSelectInput
                label={t('common:related_order')}
                placeholder={t('common:choose_order')}
                name="parentRefId"
                onAfterDetailChange={onRelatedOrderAfterChange}
              />
            </HalfContainer>
          </SectionContainer>
          <InformationField />
          <PurchaseDetailField
            travelDocument={travelDocument}
            defaultValues={props.defaultValues}
          />
          <SeparatorVertical $margin={24} />
          <DocumentDetailField />
          <SeparatorVertical $margin={24} />

          <OtherPriceComponent
            type="document-purchases"
            data={props.travelDocument}
          />
          <FormDivider />
        </div>
        <BottomSection documentPurchases={travelDocument} />
      </FormContent>
    </Form>
  );
}
